import React from 'react';
import { Avatar, Button, Skeleton, Tabs, Tag } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear, faUser } from '@fortawesome/pro-solid-svg-icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { getUsers } from '../../services/users';
import { useAuthStore } from '../../store/authStore';
import { colors } from '../../theme/colors';
import './Profile.css';

const Profile: React.FC = () => {
  const { user } = useAuthStore();
  const {
    data: workspaceUsers = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['users'],
    queryFn: getUsers,
    enabled: Boolean(user?.id),
  });

  const profile = workspaceUsers.find((member) => member.id === user?.id);
  const displayUser = profile || user;

  const stats = [
    { label: 'Tasks created', value: profile?._count?.createdTasks || 0 },
    { label: 'Tasks assigned', value: profile?._count?.assignedTasks || 0 },
    { label: 'Issues created', value: profile?._count?.createdIssues || 0 },
    { label: 'Issues assigned', value: profile?._count?.assignedIssues || 0 },
    { label: 'Projects owned', value: profile?._count?.ownedProjects || 0 },
  ];

  if (!displayUser) {
    return (
      <main className="profile-page" style={{ background: colors.bgBase }}>
        <div className="profile-state">
          <strong>Profile is unavailable</strong>
          <span>Sign in again to load your workspace profile.</span>
        </div>
      </main>
    );
  }

  return (
    <main className="profile-page" style={{ background: colors.bgBase }}>
      <header className="profile-page-header">
        <div>
          <h1>Profile</h1>
          <p>Your workspace identity, access, and activity.</p>
        </div>
      </header>

      <section className="profile-identity" aria-label="Profile identity">
        <Avatar size={72} src={displayUser.photoUrl} icon={<FontAwesomeIcon icon={faUser} />} />
        <div className="profile-identity-copy">
          <div className="profile-name-line">
            <h2>{displayUser.name}</h2>
            <Tag bordered={false} className="profile-role-chip">
              {displayUser.role.toLowerCase().replace(/^./, (letter) => letter.toUpperCase())}
            </Tag>
          </div>
          <span>{displayUser.email}</span>
          <span>{displayUser.department || 'Department not set'}</span>
        </div>
      </section>

      <section className="profile-summary" aria-label="Activity summary">
        {stats.map((stat) => (
          <div className="profile-summary-item" key={stat.label}>
            <span>{stat.label}</span>
            <strong>{isLoading ? '—' : stat.value}</strong>
          </div>
        ))}
      </section>

      {isError ? (
        <div className="profile-state">
          <strong>Activity could not be loaded</strong>
          <span>Your identity is still available, but workspace totals are temporarily unavailable.</span>
          <Button onClick={() => refetch()}>Try again</Button>
        </div>
      ) : isLoading ? (
        <div className="profile-loading"><Skeleton active paragraph={{ rows: 7 }} /></div>
      ) : (
        <Tabs
          className="profile-tabs"
          defaultActiveKey="details"
          items={[
            {
              key: 'details',
              label: 'Details',
              children: (
                <dl className="profile-details">
                  <div><dt>Full name</dt><dd>{displayUser.name}</dd></div>
                  <div><dt>Email</dt><dd>{displayUser.email}</dd></div>
                  <div><dt>Department</dt><dd>{displayUser.department || 'Not set'}</dd></div>
                  <div><dt>Role</dt><dd>{displayUser.role}</dd></div>
                  <div><dt>Manager</dt><dd>{profile?.manager?.name || 'No manager'}</dd></div>
                  <div><dt>User ID</dt><dd className="profile-monospace">{displayUser.id}</dd></div>
                  <div><dt>Account created</dt><dd>{profile?.createdAt ? dayjs(profile.createdAt).format('MMM D, YYYY') : 'Not available'}</dd></div>
                  <div><dt>Last updated</dt><dd>{profile?.updatedAt ? dayjs(profile.updatedAt).format('MMM D, YYYY') : 'Not available'}</dd></div>
                  <div><dt>Last active</dt><dd>{profile?.lastActiveAt ? dayjs(profile.lastActiveAt).format('MMM D, YYYY, h:mm A') : 'Never'}</dd></div>
                </dl>
              ),
            },
            {
              key: 'activity',
              label: 'Activity',
              children: (
                <div className="profile-tab-section">
                  <h3>Activity history</h3>
                  <p>The summary above reflects your current workspace totals.</p>
                  <div className="profile-empty-line">
                    A detailed activity timeline is not available yet.
                  </div>
                </div>
              ),
            },
            {
              key: 'settings',
              label: (
                <span><FontAwesomeIcon icon={faGear} /> Settings</span>
              ),
              children: (
                <div className="profile-settings">
                  <div className="profile-setting-row">
                    <div>
                      <h3>Profile details</h3>
                      <p>Name and department changes are managed by a workspace administrator.</p>
                    </div>
                    <span>Admin managed</span>
                  </div>
                  <div className="profile-setting-row">
                    <div>
                      <h3>Notifications</h3>
                      <p>Task and issue mentions are delivered in-app and by email.</p>
                    </div>
                    <span>Enabled</span>
                  </div>
                  <div className="profile-setting-row">
                    <div>
                      <h3>Appearance</h3>
                      <p>The workspace currently uses the dark interface.</p>
                    </div>
                    <span>Dark</span>
                  </div>
                </div>
              ),
            },
          ]}
        />
      )}
    </main>
  );
};

export default Profile;
